import { useHistory } from 'react-router-dom';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, SxProps, Typography } from '@mui/material';
import React, { ReactNode } from 'react';
import Grid from '@mui/material/Unstable_Grid2';
import Loading from '../Loading';

export interface CustomDialogProps {
    children: ReactNode;
    title?: string | ReactNode;
    description?: string;
    backCopy?: string;
    closeCopy?: string;
    onClose?: () => void;
    onBack?: () => void;
    isModal?: boolean;
    processing?: boolean;
    processingCopy?: string;
    error?: boolean;
    errorContent?: ReactNode;
    successContent?: ReactNode;
    success?: boolean;
    alternative?: ReactNode;
    successUsesMain?: boolean;
    sx?: SxProps;
}

const CustomDialog = ({
    children,
    title,
    description,
    closeCopy,
    onClose,
    onBack,
    backCopy,
    isModal,
    processing,
    processingCopy,
    errorContent,
    error,
    success,
    successContent,
    alternative,
    successUsesMain,
    sx = {},
}: CustomDialogProps) => {
    const history = useHistory();

    const handleClose = () => {
        if (!isModal) {
            if (onClose) {
                onClose();
            } else {
                history.push('/');
            }
        }
    };

    const handleBack = () => {
        if (onBack) {
            onBack();
        }
    };

    return (
        <Dialog
            disableScrollLock={true}
            sx={{
                'overflowY': 'auto',
                '::-webkit-scrollbar': {
                    display: 'none',
                },
                'msOverflowStyle': 'none',
                'scrollbarWidth': 'none',
                ...sx,
            }}
            maxWidth='sm'
            fullWidth
            open={true}
            onClose={handleClose}
        >
            {title && !processing ? (
                <>
                    <Box
                        sx={{
                            textAlign: 'center',
                            padding: {
                                xs: '1.2em 2em .75em',
                                sm: '1.2em 1.2em',
                            }
                        }}
                    >
                        <Typography variant={'h3'}>
                            {typeof title === 'string' ? `${title}${error ? ' error' : ''}` : title}
                        </Typography>
                    </Box>
                    {alternative ? (
                        <Box
                            sx={{
                                padding: '.5em 2em 1em',
                                textAlign: 'center',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '.9em',
                                }}
                                variant='body1'
                            >
                                {alternative}
                            </Typography>
                        </Box>
                    ) : null}
                </>
            ) : null}
            <DialogContent>
                {processing ? (
                    <Grid>
                        <Grid xs={12}>
                            <Loading text={processingCopy ?? 'Processing...'}/>
                        </Grid>
                    </Grid>
                ) : null}
                {!processing && error ? (
                    <Grid>
                        <Grid xs={12}>{errorContent}</Grid>
                    </Grid>
                ) : null}
                {!processing && success ? (
                    <Grid>
                        <Grid xs={12}>{successUsesMain ? children : successContent}</Grid>
                    </Grid>
                ) : null}
                {!processing && !error && !success ? (
                    <>
                        {description ? <DialogContentText>{description}</DialogContentText> : null}
                        {children}
                    </>
                ) : null}
            </DialogContent>
            {!processing ? (
                <DialogActions>
                    {onBack || backCopy ? (
                        <Button
                            variant='contained'
                            size={'small'}
                            color={'secondary'}
                            onClick={handleBack}
                            sx={{
                                marginRight: 'auto',
                            }}
                        >
                            {backCopy || 'Back'}
                        </Button>
                    ) : null}
                    <Button size={'small'} variant='text' color={'secondary'} onClick={handleClose}>
                        {closeCopy || 'Close'}
                    </Button>
                </DialogActions>
            ) : null}
        </Dialog>
    );
};

export default CustomDialog;
